import { useEffect, useState } from 'react';
import { CreditCard, Loader2 } from 'lucide-react';
import { apiFetchJson } from '../lib/api';

interface KpayPayNowProps {
  orderNum: string;
  /** KPAY_LIVE=0（回滾保護）時渲染的舊人工付款 UI */
  fallback?: React.ReactNode;
}

/**
 * KPay 立即付款卡——線上支付開通（KPAY_LIVE=1）後訂單頁嘅唯一付款路徑（Captain 2026-10-02：
 * 全自動化，唔開放任何人工付款渠道）。付款後由 webhook／查詢補單自動確認。
 */
export default function KpayPayNow({ orderNum, fallback }: KpayPayNowProps) {
  const [live, setLive] = useState<boolean | null>(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetchJson<{ kpay_live?: string }>('/public/settings')
      .then((res) => setLive(Boolean(res.success && res.data?.kpay_live === '1')))
      .catch(() => setLive(false));
  }, []);

  const startPay = async () => {
    setPaying(true);
    setError('');
    try {
      const res = await apiFetchJson<{ payOrigin: string; payPath: string }>(
        `/public/orders/${orderNum}/kpay/checkout`,
        { method: 'POST', body: JSON.stringify({}) }
      );
      if (res.success && res.data) {
        window.location.href = `${res.data.payOrigin}${res.data.payPath}`;
        return;
      }
      setError(res.error || '無法建立付款，請稍後再試');
    } catch {
      setError('網絡錯誤，請稍後再試');
    } finally {
      setPaying(false);
    }
  };

  if (live === null) return null;
  if (!live) return <>{fallback}</>;

  return (
    <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6">
      <h3 className="text-lg font-bold text-amber-900 mb-2 flex items-center gap-2">
        <CreditCard size={22} />
        線上付款（自動確認）
      </h3>
      <p className="text-stone-600 text-sm mb-4">
        信用卡 · Apple Pay · Google Pay · AlipayHK · 微信 · 支付寶——付款後訂單自動確認，唔使等。
      </p>
      <button
        onClick={startPay}
        disabled={paying}
        className="w-full bg-x1493-primary text-white text-lg font-bold py-4 rounded-xl hover:bg-red-700 disabled:opacity-60 transition flex items-center justify-center gap-2"
      >
        {paying ? <Loader2 size={22} className="animate-spin" /> : <CreditCard size={22} />}
        {paying ? '正在建立付款...' : `立即付款`}
      </button>
      {error && <p className="text-red-600 text-sm mt-3 text-center">{error}</p>}
    </div>
  );
}
